// The store detail header: port of RecordHeaderView (RecordDetailView.swift) on the soft hero backdrop. Top line
// (type, Do not contact, Archived, newer app, lead source), name, company and address, stage / priority / value chips, owner
// and territory, then visits · days since visit · next scheduled or "Overdue". The NBA score ring is left out: the
// score is computed on each phone and never synced (WEB_PLAN.md §3.10).
//
// Beside it a mini MapCanvas with the store's pin in the company's default pin coloring (iOS MapSnippet). MapCanvas
// reads its center once, at creation, and fits only on its first data, so a pin moved on an iPhone while this page is
// open is followed with flyTo.

import { CalendarClock, CircleAlert, Hand, Map as MapIcon, MapPinned, MapPinOff } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router';
import type { LeadSourcePayload, RecordPayload, TerritoryPayload } from '@/contracts/models';
import type { DataItem } from '@/data/hooks';
import type { RecordStats } from '@/domain/derived';
import { visitCountText } from '@/domain/format';
import { isValidCoordinate } from '@/domain/geo';
import { stageFor } from '@/domain/pipelines';
import { pinColorHex, recordDisplayName, recordSubtitle, addressOneLine } from '@/domain/records';
import { lookup } from '@/domain/util';
import type { MapColorize } from '@/contracts/enums';
import {
  DaysSinceVisit,
  MapCanvas,
  OwnerAvatar,
  PriorityChip,
  RecordTypeBadge,
  StageChip,
  storeValueText,
  visitRecency,
  type MapCanvasHandle,
  type MapPoint,
  type StoreRowLookups,
} from '@/features/common';
import { Badge, Chip, ColorDot, cn } from '@/ui';
import { nextScheduledText } from './detailModel';
import { NewerAppBadge } from './NewerAppBadge';

export interface StoreHeaderProps {
  item: DataItem<RecordPayload>;
  stats: RecordStats;
  lookups: StoreRowLookups;
  territory: TerritoryPayload | undefined;
  source: LeadSourcePayload | undefined;
  colorize: MapColorize;
  /** Territory names link to their page (managers). */
  linkTerritory: boolean;
  now: Date;
}

export function StoreHeader({ item, stats, lookups, territory, source, colorize, linkTerritory, now }: StoreHeaderProps) {
  const r = item.value;
  const name = recordDisplayName(r);
  const subtitle = recordSubtitle(r);
  const stage = stageFor(r, lookups.pipelines);
  const owner = lookup(lookups.users, r.ownerId);
  const value = storeValueText(r.value, lookups.currencyCode, lookups.locale);
  const next = nextScheduledText(stats.nextScheduledAt, now);

  return (
    <section
      aria-labelledby="store-name"
      className="gt-hero-soft grid gap-5 overflow-hidden rounded-card border border-line p-5 shadow-card lg:grid-cols-[minmax(0,1fr)_320px]"
    >
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <RecordTypeBadge type={r.type} size={32} label={null} />
          <RecordTypeBadge type={r.type} variant="label" />
          {r.doNotContact && (
            <Chip color="#DB4038" filled icon={<Hand aria-hidden />}>
              Do not contact
            </Chip>
          )}
          {r.isArchived && (
            <Badge tone="neutral" size="sm">
              Archived
            </Badge>
          )}
          {item.undecodable !== undefined && <NewerAppBadge long />}
          <span className="flex-1" />
          {source && <span className="truncate text-xs text-fg-muted">Source: {source.name}</span>}
        </div>

        <div className="min-w-0">
          <h1 id="store-name" className="text-2xl leading-tight font-bold tracking-tight break-words text-fg">
            {name}
          </h1>
          {/* Leads and accounts: the subtitle is the address, so the company goes here (unless it is the title). */}
          {r.type !== 'contact' && r.name !== '' && r.company !== '' && r.company !== r.name && (
            <p className="mt-1 text-sm font-medium text-fg">{r.company}</p>
          )}
          {subtitle !== '' && <p className="mt-1 text-sm text-fg-muted">{subtitle}</p>}
          {r.type === 'contact' && addressOneLine(r.address) !== '' && (
            <p className="mt-0.5 text-xs text-fg-muted">{addressOneLine(r.address)}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StageChip stage={stage} filled emptyLabel="No stage" size="md" />
          <PriorityChip priority={r.priority} showTitle tinted size="md" />
          {value && (
            <Chip color="#102A5E" size="md">
              <span className="tabular-nums">{value}</span>
            </Chip>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          <OwnerAvatar
            user={owner}
            presence={owner ? lookup(lookups.presence, owner.id) : undefined}
            showName
            role="Owner"
            size="sm"
          />
          {territory && (
            <span className="inline-flex min-w-0 items-center gap-1.5 text-fg-muted">
              <MapIcon aria-hidden className="size-4" />
              <span className="sr-only">Territory </span>
              <ColorDot color={territory.colorHex} size={8} />
              {linkTerritory ? (
                <Link to={`/territories/${territory.id.toLowerCase()}`} className="truncate font-medium text-link hover:underline">
                  {territory.name}
                </Link>
              ) : (
                <span className="truncate font-medium text-fg">{territory.name}</span>
              )}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-medium text-fg-muted">
          <span className="inline-flex items-center gap-1.5">
            <MapPinned aria-hidden className="size-4" />
            {visitCountText(stats.visitCount)}
            {stats.verifiedVisitCount > 0 && <span className="text-success-text">· {stats.verifiedVisitCount} verified</span>}
          </span>
          <DaysSinceVisit recency={visitRecency(stats.lastVisitAt, lookups.now)} />
          {next && (
            <span
              className={cn(
                'inline-flex items-center gap-1.5',
                next.overdue && 'rounded-full bg-danger px-2 py-0.5 font-semibold text-white',
              )}
            >
              {next.overdue ? <CircleAlert aria-hidden className="size-3.5" /> : <CalendarClock aria-hidden className="size-4" />}
              {next.text}
            </span>
          )}
        </div>
      </div>

      <MiniMap record={r} colorize={colorize} lookups={lookups} name={name} />
    </section>
  );
}

const MINI_MAP_ZOOM = 15;

function MiniMap({ record, colorize, lookups, name }: { record: RecordPayload; colorize: MapColorize; lookups: StoreRowLookups; name: string }) {
  const coordinate = isValidCoordinate(record.coordinate) ? record.coordinate : undefined;
  const color = pinColorHex(record, colorize, lookups);
  const points = useMemo<MapPoint[]>(
    () => (coordinate ? [{ id: record.id, coordinate, color }] : []),
    // The coordinate object changes only with the record payload.
    [record.id, coordinate, color],
  );
  const mapRef = useRef<MapCanvasHandle>(null);
  const lat = coordinate?.latitude;
  const lng = coordinate?.longitude;
  const shown = useRef<string | null>(null);
  useEffect(() => {
    const key = lat === undefined || lng === undefined ? null : `${lat},${lng}`;
    // The first position is the creation center; only later moves need the camera.
    if (key && shown.current && key !== shown.current) mapRef.current?.flyTo({ latitude: lat!, longitude: lng! }, MINI_MAP_ZOOM);
    shown.current = key;
  }, [lat, lng]);
  if (!coordinate) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-tile border border-dashed border-line-strong bg-surface/60 p-4 text-center text-sm text-fg-muted">
        <MapPinOff aria-hidden className="size-6 text-fg-subtle" />
        No pin on the map yet.
      </div>
    );
  }
  return (
    <div className="h-[220px] min-h-[200px] overflow-hidden rounded-tile border border-line lg:h-full">
      <MapCanvas
        ref={mapRef}
        ariaLabel={`Map showing ${name}`}
        points={points}
        cluster={false}
        interactive={false}
        showNavigation={false}
        initialCenter={coordinate}
        initialZoom={MINI_MAP_ZOOM}
      />
    </div>
  );
}
